import { createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { api } from "../Config/Api";
import { ExtractErrorInfo } from "../components/shared/ExtractErrorInfo";
import { RootState } from "../store/store";
import { ErrorType } from "../types/errorType";

export interface AppTypeWithData<T> {
  loading: boolean;
  progressiveLoading: boolean;
  progress: number;
  error: ErrorType | null;
  data: T[];
  totalRecords: number;
  totalPages: number;
  pageSize: number;
  apiPageSize: number;
  page: number;
  currentPage: number;
  isInitialLoadComplete: boolean;
}

export const initialState = <T>(): AppTypeWithData<T> => ({
  loading: false,
  progressiveLoading: false,
  progress: 0,
  error: null,
  data: [],
  totalRecords: 0,
  totalPages: 0,
  pageSize: 10,
  apiPageSize: 500,
  currentPage: 0,
  page: 0,
  isInitialLoadComplete: false,
});

export interface FetchActionParams {
  organisationId: string;
  apiPageSize: number;
  page?: number;
  userId?: string | undefined;
}

export interface ApiResponse<T> {
  results: T[];
  totalRecords: number;
  totalPages: number;
  page: number;
  pageSize: number;
  firstPage: string;
  prevPage: string;
  nextPage: string;
  lastPage: string;
}

export const constructApiUrl = (
  baseUrl: string,
  organisationId: string,
  page: number,
  pageSize: number,
  userId?: string
): string => {
  const query = `${baseUrl}?organisationId=${organisationId}&Page=${page}&PageSize=${pageSize}${
    userId ? `&userId=${userId}` : ""
  }`;
  return query;
};

export const createFetchInitialDataThunk = <T>(name: string, baseUrl: string) =>
  createAsyncThunk<ApiResponse<T>, FetchActionParams>(
    `${name}/fetchInitialData`,
    async (
      {
        organisationId,
        page = 1,
        apiPageSize,
        userId,
      }: { organisationId: string; page?: number; apiPageSize: number; userId?: string },
      { rejectWithValue }
    ) => {
      try {
        const { data } = await api.get<ApiResponse<T>>(
          constructApiUrl(baseUrl, organisationId, page, apiPageSize, userId)
        );

        //TODO Remove - temporarily log response size
        // const jsonString = JSON.stringify(data);
        // const byteLength = new TextEncoder().encode(jsonString).length;
        // const kbSize = byteLength / 1024;
        // console.log(
        //   `Response size: ${kbSize.toFixed(2)} KB - with ${data.results.length} records - Max ${apiPageSize} records`
        // );

        return data;
      } catch (error: any) {
        const errorInfo = ExtractErrorInfo(error);
        return rejectWithValue(errorInfo);
      }
    }
  );

export const createFetchProgressiveDataThunk = <T>(
  name: string,
  baseUrl: string,
  selectTotalRecords: (state: RootState) => number,
  selectLoadedRecords: (state: RootState) => number,
  appendData: (data: T[]) => PayloadAction<T[]>,
  setProgress: (progress: number) => PayloadAction<number>
) =>
  createAsyncThunk<void, FetchActionParams>(
    `${name}/fetchProgressiveData`,
    async (
      { organisationId, apiPageSize, userId }: { organisationId: string; apiPageSize: number; userId?: string },
      { dispatch, getState, rejectWithValue }
    ) => {
      try {
        const state = getState() as RootState;
        const totalRecords = selectTotalRecords(state);
        const loadedRecords = selectLoadedRecords(state);
        const totalPages = Math.ceil(totalRecords / apiPageSize);
        let accumulatedData: T[] = [];
        let totalRecordsFetched = loadedRecords;

        let pageToStartFrom = Math.ceil(loadedRecords / apiPageSize) + 1;

        for (let page = pageToStartFrom; page <= totalPages; page++) {
          const { data } = await api.get<ApiResponse<T>>(
            constructApiUrl(baseUrl, organisationId, page, apiPageSize, userId)
          );
          accumulatedData = [...accumulatedData, ...data.results];
          totalRecordsFetched += data.results.length;
          const progress = Math.min((totalRecordsFetched / totalRecords) * 100, 100);
          dispatch(setProgress(progress));
          console.log(`Fetched page ${page} with ${data.results.length} records, progress: ${progress}%`);
        }

        dispatch(appendData(accumulatedData));
        dispatch(setProgress(100));
      } catch (error: any) {
        const errorInfo = ExtractErrorInfo(error);
        return rejectWithValue(errorInfo);
      }
    }
  );
